import { Controller, FormProvider, useForm } from 'react-hook-form';
import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Message } from 'semantic-ui-react';
import { Form } from 'components/form-elements/Form';
import { LabeledInput } from 'components/form-elements/inputs/LabeledInput';
import { ShouldRender } from 'components/ShouldRender';
import { Brikk } from 'components/Brikk';
import { Text } from 'components/typography';
import { MultiSelect } from 'components/form-elements/selects/MultiSelect';
import { useModal } from 'components/../store/modals';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGetUsers } from 'services/2n/get-all-users';
import { useUpdateGroup } from 'services/2n/groups';
import { useUserPermissions } from 'services/user/permissions';
import { useAccessManagement2NModal } from '../../../AccessManagement2NModalContext/useAccessManagement2NModal';
import { useGroupFormSchema } from '../validation';
import styles from './styles.module.scss';
import { FormActions } from './components/FormActions';

export const GroupForm = () => {

  const accessManagement2NModal = useAccessManagement2NModal();
  const selectedGroup = accessManagement2NModal.selected.group;
  const { users } = useGetUsers();
  const usersInThisGroup = users.map((user) => ({ value: user.Id, label: user.Name, email: user.Email }));
  const selectedGroupMembers = (selectedGroup && selectedGroup.Members)
    ? selectedGroup.Members.map((m) => ({ value: m.Id, label: m.Name, email: m.Email }))
    : [];
  const modal = useModal();
  const intl = useIntl();

  const groupFormSchema = useGroupFormSchema();

  const formMethods = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(groupFormSchema),
    defaultValues: {
      name: selectedGroup && selectedGroup.Name ? selectedGroup.Name : '',
      usersInThisGroup: selectedGroupMembers,
    },
  });

  const { handleSubmit, control, watch, reset, formState } = formMethods;

  useEffect(() => {
    if (!selectedGroup) return;
    reset({
      name: selectedGroup.Name || '',
      usersInThisGroup: selectedGroupMembers,
    });
  }, [selectedGroup]);

  const { errors } = formState || {};

  const { mutateAsync: updateGroup, isLoading } = useUpdateGroup();
  const { checkPermission } = useUserPermissions();
  const isAdmin = checkPermission('admin-users', 'create');
  const isSystemAdmin = checkPermission('system-admin', 'create');

  const onSubmit = async (data) => {
    if (!selectedGroup) return;
    const payload = {
      name: data.name,
      users: (data.usersInThisGroup || []).map((u) => ({ id: u.value })),
    };
    await updateGroup({ id: selectedGroup.Id, group: payload });
  };

  useEffect(() => {
    if (formState.isDirty) {
      accessManagement2NModal.updateSavedChanges('group', formState.isDirty);
    }
  }, [formState.isDirty]);

  return (
    <div className={styles.formContainer}>
      <FormProvider {...formMethods}>
        <Brikk.Heading
          heading="accessGroupsEdit"
        />
        <ShouldRender condition={selectedGroup && Object.hasOwn(selectedGroup, 'Name')}>
          <Text
            element="h2"
            thin
            xl
            size={20}
          >
            {String((selectedGroup && watch('name')) || '')}
          </Text>
        </ShouldRender>
        {selectedGroup ? (
 
          <Form
            className={styles.form}
            onSubmit={handleSubmit(onSubmit)}
          >

            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <LabeledInput
                  labelIntl="accessGroupsName"
                  id="name"
                  value={field.value}
                  placeholder={isAdmin && !isSystemAdmin ? 'GH-XXXXXXX' : ''}
                  onChange={(event) => {
                    if (isAdmin && !isSystemAdmin) {
                      const inputValue = event.target.value;
                      if (inputValue === '' || inputValue.toUpperCase() === 'GH-') {
                        field.onChange('');
                        return;
                      }
                      if (!inputValue.toUpperCase().startsWith('GH')) {
                        field.onChange(`GH-${inputValue}`);
                      } else {
                        field.onChange(inputValue);
                      }
                    } else {
                      field.onChange(event.target.value);
                    }
                  }}
                  hasError={!!(errors && errors.name)}
                />
              )}
            />
            <ShouldRender condition={errors && errors.name}>
              <Message negative>
                <Message.Content>
                  <FormattedMessage id={errors.name?.message} />
                </Message.Content>
              </Message>
            </ShouldRender>

            <Controller
              name="usersInThisGroup"
              control={control}
              render={({ field }) => {

                const handleChange = (newValue) => {
                  const oldValue = field.value || [];
                  const newArr = newValue || [];

                  const removed = oldValue.filter(oldItem => !newArr.some(n => n.value === oldItem.value));
                  if (removed.length > 0) {
                    modal.openModal({
                      modalType: 'ConfirmationModal',
                      modalProps: {
                        title: intl.formatMessage({ id: 'removeUser' }),
                        content: intl.formatMessage(
                          { id: 'accessGroupsRemoveUserConfirm' },
                        ),
                        onConfirm: () => field.onChange(newValue),
                      },
                    });
                  } else {
                    field.onChange(newValue);
                  }
                };

                return (
                  <>
                    <MultiSelect
                      options={usersInThisGroup}
                      title="accessGroupsUsersInGroup"
                      value={field.value}
                      onChange={handleChange}
                    />
                    <ShouldRender condition={errors && errors.usersInThisGroup}>
                      <Message negative>
                        <Message.Content>
                          <FormattedMessage id={errors.usersInThisGroup?.message} />
                        </Message.Content>
                      </Message>
                    </ShouldRender>
                  </>
                );
              }}
            />
          </Form>

        ) : (
          <Text
            secondary
            center
            intl="accessGroupsNoGroupSelected"
            size={16}
          />
        )}
        {selectedGroup && (
        <FormActions
          isLoading={isLoading}
          onSubmit={onSubmit}
        />
        )}
      </FormProvider>
    </div>
  );
};
